import { CheckCircleOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, message, Divider, Tooltip, Tag, Spin, Breadcrumb } from 'antd';
import React, { useState, useRef, Fragment } from 'react';
import { PageContainer, FooterToolbar } from '@ant-design/pro-layout';
import type { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import { groups, deleteGroups } from '@/services/api';
import type { API } from '@/services/typings';
import { t, renderDetails, getTablePaginationConfig, GroupTypeUser, GrossGropType_UserGroupToUserGroup, NamesPurpose_EditUserGroup } from '../../global';
import NewOrUpdateForm from './NewOrUpdateForm';
import styles from './index.less';
import { Link, useIntl, useModel } from '@umijs/max';
import { getLocalSortState, handleSortStateChange } from '@/components/Others/table_sort';
import MyModalConfirm from '@/components/Others/MyModalConfirm';
import { fetchAllGroups } from '../DeviceGroupList';
import { hasPermUserGroupsEdit, hasPermToViewUsersInGroup } from '../RoleList/permissionUtils';

export const GROUP_LIST_SORT_STATE_KEY = 'group_list_sort_state';

const handleDelete = async (rows: API.GroupListItem[]) => {
  const hide = message.loading(t('Deleting'));
  try {
    await deleteGroups({
      data: rows.map((x) => x.guid),
    });
    hide();

    message.success(t('Deletion is successful'));
    return true;
  } catch (error) {
    hide();
    message.error(
      typeof error == 'string' ? (error as string) : t('Deletion failed, please try again!'),
    );
    return false;
  }
};

export const LoadingSpinner: React.FC = () => {
  return (
    <div style={{
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Spin size="large" />
    </div>
  );
};

const TableList: React.FC = () => {
  const [createModalVisible, handleModalVisible] = useState<boolean>(false);
  const [updateModalVisible, handleUpdateModalVisible] = useState<boolean>(false);

  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const actionRef = useRef<ActionType>();

  const [allGroups, setAllGroups] = useState<string[]>([]);
  const [currentRow, setCurrentRow] = useState<API.GroupListItem>();
  const [selectedRowsState, setSelectedRows] = useState<API.GroupListItem[]>([]);
  const [sortState, setSortState] = useState<API.SortState>(() => getLocalSortState(GROUP_LIST_SORT_STATE_KEY));
  const { initialState } = useModel('@@initialState');
  const canEdit = hasPermUserGroupsEdit(initialState?.user);


  const intl = useIntl();
  const plain = (text: string, defaultMessage = '') => t(`pages.GroupList.${text}`, true, defaultMessage || text, intl) as string;

  const DeleteGroups = (props: { children: any; onClick: () => void }) => (
    <MyModalConfirm
      title={t('deleteConfirm', true, 'Are you sure to delete?')}
      onConfirm={() => props.onClick()}
      okText="Yes"
      cancelText="No"
    >
      {props.children}
    </MyModalConfirm>
  );

  const outgoingTagColor = '#84A13D';
  const incomingTagColor = '#399C97';
  const makeAllowedOutgoings = (record: API.GroupListItem) => record.access_to.filter(x => x.type === GrossGropType_UserGroupToUserGroup).length > 0 && <>
    <div>
      &nbsp;
      {
        record.access_to.filter(x => x.type === GrossGropType_UserGroupToUserGroup).map((x) => x.name).sort().map((x) => {
          return <Tag key={'outgoings'} color={outgoingTagColor} className={styles.listCrossGroupItems}>{x}</Tag>;
        })
      }
    </div>
  </>;

  const makeAllowedIncomings = (record: API.GroupListItem) => record.accessed_from.length > 0 && <>
    <div>
      &nbsp;
      {
        record.accessed_from.map((x) => x.name).sort().map((x) => {
          return <Tag key={'incomings'} color={incomingTagColor} className={styles.listCrossGroupItems}>{x}</Tag>;
        })
      }
    </div>
  </>;

  const columns: ProColumns<API.GroupListItem>[] = [
    {
      title: t('Name'),
      dataIndex: 'name',
      render: (dom, entity) => {
        if (!hasPermToViewUsersInGroup(initialState?.user, entity.name)) {
          return <span>{entity.name}</span>;
        }
        return (
          <Link to={`/groups/user/${entity.guid}`}>{entity.name}</Link>

        );
      },
      key: 'name',
      sorter: true,
      sortOrder: sortState.columnKey === 'name' ? sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Created Time'),
      dataIndex: 'created_at',
      valueType: 'dateTime',
      render: (text, record) => new Date((record.created_at || 0) * 1000).toLocaleString(),
      search: false,
      hideInTable: true,
    },
    {
      title: t('Note'),
      dataIndex: 'note',
      search: false,
      key: 'note',
      sorter: true,
      sortOrder: sortState.columnKey === 'note' ? sortState.order : undefined,
      showSorterTooltip: false,
    },
    {
      title: t('Within-group connections', true),
      dataIndex: ['no_conn_in_group'],
      render: (text, record) => {
        if (record.info?.no_conn_in_group === 1) {
          return (
            <Tooltip title={t('Disallowed')}>
              <CloseCircleOutlined style={{ color: '#f5222d' }} />
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title={t('Allowed')}>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          );
        }
      },
      key: 'info_no_conn_in_group',
      sorter: true,
      sortOrder: sortState.columnKey === 'info_no_conn_in_group' ? sortState.order : undefined,
      showSorterTooltip: false,
      hideInSearch: true,
    },
    {
      title: t('Within-group connections', true),
      dataIndex: ['no_conn_in_group'],
      valueEnum: {
        0: {
          text: t('Allow'),
        },
        1: {
          text: t('Disallow'),
        },
      },
      hideInTable: true,
    },
    {
      title: <div>
        {t('Cross-group connections', true)}
        <div style={{ height: '8px' }} />
        <Tag color={outgoingTagColor} className={styles.titleTag}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {plain('To')}
          </div>
        </Tag>
        <Tag color={incomingTagColor} className={styles.titleTag}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {plain('From')}
          </div>
        </Tag>
      </div>,
      search: false,
      render: (text, record) => {
        return <>
          {makeAllowedOutgoings(record)}
          {
            record.access_to.filter(x => x.type === GrossGropType_UserGroupToUserGroup).length > 0 && record.accessed_from.length > 0 && <div style={{ height: '8px' }} />
          }
          {makeAllowedIncomings(record)}
        </>;
      },
    },
    {
      title: t('Action'),
      search: false,
      hideInTable: !canEdit,
      render: (text, record) => record.name == 'Default' ? <></> : (
        <Fragment>
          <a
            onClick={async () => {
              setCurrentRow(record);
              setLoading(true);
              try {
                const grps = await fetchAllGroups('%', NamesPurpose_EditUserGroup);
                setAllGroups(grps);
                setLoading(false);
                handleUpdateModalVisible(true);
              } catch (e) {
                setLoading(false);
                message.error(typeof e === 'string' ? e : 'Failed to fetch group names.');
              }
            }}
          >
            {t('Edit')}
          </a>
          <Divider type="vertical" />
          <DeleteGroups
            onClick={async () => {
              setCurrentRow(record);
              await handleDelete([record]);
              actionRef.current?.reloadAndRest?.();
            }}
          >
            <a style={{ color: 'red' }}>{t('Delete')}</a>
          </DeleteGroups>
        </Fragment>
      ),
    },
  ];

  return (
    <PageContainer
      breadcrumbRender={() => (
        <Breadcrumb items={[
          { title: t('menu.list.group-list', true) },
          { title: t('menu.list.group-list.user', true) },
        ]} />
      )}
    >
      {loading && (
        <LoadingSpinner />
      )}
      <ProTable<API.GroupListItem, API.PageParams & { type: string }>
        headerTitle={t('Group List')}
        columnsState={{ persistenceType: 'localStorage', persistenceKey: 'user_grouplist_columns_state' }}
        actionRef={actionRef}
        rowKey="guid"
        search={{
          labelWidth: 0,
        }}
        toolBarRender={canEdit ? () => [
          <Button
            type="primary"
            key="primary"
            onClick={async () => {
              setLoading(true);
              try {
                const grps = await fetchAllGroups('%', NamesPurpose_EditUserGroup);
                setAllGroups(grps);
                setLoading(false);
                handleModalVisible(true);
              } catch (e) {
                setLoading(false);
                message.error(typeof e === 'string' ? e : 'Failed to fetch group names.');
              }
            }}
          >
            <PlusOutlined /> {t('Create')}
          </Button>,
        ] : undefined}
        request={async (params) => {
          const res = await groups(params, undefined, undefined, sortState);
          return res;
        }}
        params={{ type: GroupTypeUser.toString() }}
        columns={columns}
        rowSelection={canEdit ? {
          onChange: (_, selectedRows) => {
            setSelectedRows(selectedRows);
          },
        } : undefined}
        pagination={getTablePaginationConfig("group_list")}
        onChange={(_, __, sorter) => {
          handleSortStateChange(sorter, actionRef, GROUP_LIST_SORT_STATE_KEY, setSortState);
        }}
      />
      {selectedRowsState?.length > 0 && (
        <FooterToolbar
          extra={
            <div>
              {t('Chosen')} <a style={{ fontWeight: 600 }}>{selectedRowsState.length}</a>{' '}
              {t('Items')}
            </div>
          }
        >
          <DeleteGroups
            onClick={async () => {
              await handleDelete(selectedRowsState);
              setSelectedRows([]);
              actionRef.current?.reloadAndRest?.();
            }}
          >
            <Button danger>{t('Delete')}</Button>
          </DeleteGroups>
        </FooterToolbar>
      )}
      <NewOrUpdateForm
        isNew={true}
        visible={createModalVisible}
        allGroups={allGroups}
        onVisibleChange={handleModalVisible}
        actionRef={actionRef}
        initialValues={undefined}
        handleModalVisible={handleModalVisible}
        handleUpdateModalVisible={handleUpdateModalVisible}
      />
      <NewOrUpdateForm
        isNew={false}
        visible={updateModalVisible}
        allGroups={allGroups}
        onVisibleChange={handleUpdateModalVisible}
        actionRef={actionRef}
        initialValues={currentRow || undefined}
        handleModalVisible={handleModalVisible}
        handleUpdateModalVisible={handleUpdateModalVisible}
      />
      {renderDetails(
        showDetail,
        currentRow?.guid,
        currentRow,
        columns.slice(0, columns.length - 1),
        setCurrentRow,
        setShowDetail,
      )}
    </PageContainer>
  );
};

export default TableList;
